/**
 * size-selector.js
 *
 * Drives the featured size-selector chips rendered by snippets/size-selector.liquid.
 *
 * IMPORTANT: this file does NOT reimplement collection filtering. It builds the
 * same query-string the native facets form would build and hands it to
 * `FacetFiltersForm.renderPage(...)`, the exact static method assets/facets.js
 * already uses for every other filter (brand, price, availability, etc). That
 * keeps this widget permanently in sync with however the native filters work,
 * and it means combinations like "Talla 26 + Nike + Rebaja" just work, because
 * it's the same underlying request Shopify's own filters make.
 *
 * Persistence across collections is done with sessionStorage, which lives for
 * as long as the current browser tab/window session is open (it is cleared
 * when the tab or window is closed — it has nothing to do with Shopify
 * customer login/logout). It's only ever applied client-side, after page
 * load, through the same AJAX path above. The server-rendered URL for a bare
 * collection page is never rewritten or redirected, so there is no impact on
 * indexable URLs or canonical tags.
 */
(function () {
  var STORAGE_KEY = 'latyce:size-filter';

  function readStoredSize() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function writeStoredSize(paramName, value, label) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ paramName: paramName, value: value, label: label }));
    } catch (e) {
      /* sessionStorage unavailable (private mode, etc) — persistence simply won't work */
    }
  }

  function clearStoredSize() {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      /* no-op */
    }
  }

  function paramsFromUrl(url) {
    var queryIndex = url.indexOf('?');
    return new URLSearchParams(queryIndex === -1 ? '' : url.slice(queryIndex + 1));
  }

  var SizeSelectorWidget = (function () {
    function SizeSelectorWidget(el) {
      this.el = el;
      this.paramName = el.dataset.paramName;
      this.onChipClick = this.onChipClick.bind(this);
      this.onClearClick = this.onClearClick.bind(this);

      el.querySelectorAll('[data-size-chip]').forEach(
        function (chip) {
          chip.addEventListener('click', this.onChipClick);
        }.bind(this)
      );

      el.querySelectorAll('[data-size-clear]').forEach(
        function (btn) {
          btn.addEventListener('click', this.onClearClick);
        }.bind(this)
      );

      this.maybeApplyStoredSize();
    }

    SizeSelectorWidget.prototype.renderViaFacets = function (targetUrl) {
      if (typeof FacetFiltersForm === 'undefined') {
        // Safety net: if the native facets controller isn't available for any
        // reason, fall back to a normal navigation so the feature still works.
        window.location.href = targetUrl;
        return;
      }

      this.el.classList.add('loading');
      var searchParams = paramsFromUrl(targetUrl).toString();
      FacetFiltersForm.renderPage(searchParams, null, true);

      // renderPage swaps #ProductGridContainer's innerHTML, which lives outside
      // this custom element, so we manually refresh our own chip states shortly
      // after the fetch resolves by re-requesting just this section's markup.
      this.refreshSelf(searchParams);
    };

    SizeSelectorWidget.prototype.refreshSelf = function (searchParams) {
      var sectionId = document.getElementById('product-grid')
        ? document.getElementById('product-grid').dataset.id
        : null;
      if (!sectionId) return;

      var url = window.location.pathname + '?section_id=' + sectionId + '&' + searchParams;

      fetch(url)
        .then(function (response) {
          return response.text();
        })
        .then(
          function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var freshWidget = parsed.querySelector('size-selector-widget');
            if (freshWidget) {
              this.el.outerHTML = freshWidget.outerHTML;
            } else {
              // No size filter left to show (e.g. filter disappeared) — just drop loading state.
              this.el.classList.remove('loading');
              this.el.remove();
            }
          }.bind(this)
        )
        .catch(
          function () {
            this.el.classList.remove('loading');
          }.bind(this)
        );
    };

    SizeSelectorWidget.prototype.onChipClick = function (event) {
      event.preventDefault();
      var chip = event.currentTarget;
      var isActive = chip.dataset.active === 'true';
      var targetUrl = isActive ? chip.dataset.urlToRemove : chip.dataset.urlToAdd;

      if (isActive) {
        clearStoredSize();
      } else {
        writeStoredSize(chip.dataset.paramName, chip.dataset.value, chip.dataset.label);
      }

      this.renderViaFacets(targetUrl);
    };

    SizeSelectorWidget.prototype.onClearClick = function (event) {
      event.preventDefault();
      clearStoredSize();
      var removeAllUrl = this.el.dataset.urlToRemoveAll || window.location.pathname;
      this.renderViaFacets(removeAllUrl);
    };

    SizeSelectorWidget.prototype.maybeApplyStoredSize = function () {
      var stored = readStoredSize();
      if (!stored) return;

      // Already applied on this page load (server-rendered), nothing to do.
      var currentParams = new URLSearchParams(window.location.search);
      if (currentParams.get(stored.paramName) === stored.value) return;

      var matchingChip = this.el.querySelector(
        '[data-size-chip][data-value="' + CSS.escape(stored.value) + '"]'
      );

      if (!matchingChip || matchingChip.classList.contains('is-disabled')) {
        // This size doesn't exist (or has no stock) in the current collection.
        // We do NOT force the filter and we do NOT touch the product grid —
        // we only show a discreet, dismissible notice naming the stored size,
        // and the collection keeps rendering normally underneath it.
        var emptyEl = this.el.querySelector('[data-size-selector-empty]');
        var emptyTextEl = this.el.querySelector('[data-size-selector-empty-text]');
        if (emptyTextEl) {
          emptyTextEl.textContent = 'Tu talla ' + stored.label + ' no está disponible en esta colección.';
        }
        if (emptyEl) emptyEl.hidden = false;
        return;
      }

      if (matchingChip.dataset.active === 'true') return;

      writeStoredSize(stored.paramName, stored.value, stored.label);
      this.renderViaFacets(matchingChip.dataset.urlToAdd);
    };

    return SizeSelectorWidget;
  })();

  class SizeSelectorElement extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      new SizeSelectorWidget(this);
    }
  }

  if (!customElements.get('size-selector-widget')) {
    customElements.define('size-selector-widget', SizeSelectorElement);
  }
})();
